import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/colors';
import { useRSVP } from '../../contexts/RSVPContext';
import { EventResponses, RSVPData, RSVPEventId } from '../../services/api';

const RSVP_EVENTS: { id: RSVPEventId; title: string; description: string }[] = [
  {
    id: 'welcomeDrinks',
    title: 'Thursday Welcome Drinks',
    description: 'Let us know who will join us to kick off the weekend.'
  },
  {
    id: 'poolParty',
    title: 'Pool Party',
    description: 'Let us know who can join us by the pool.'
  },
  {
    id: 'rehearsalDinner',
    title: 'Rehearsal Dinner',
    description: 'Let us know who can attend dinner with us.'
  },
  {
    id: 'ceremony',
    title: 'Saturday Ceremony',
    description: 'Let us know who will be there for the ceremony and celebration.'
  }
];

const buildEmptyEventResponses = (names: string[]): EventResponses => ({
  welcomeDrinks: {
    partyMembers: names.map(name => ({ name, attending: false }))
  },
  poolParty: {
    partyMembers: names.map(name => ({ name, attending: false }))
  },
  rehearsalDinner: {
    partyMembers: names.map(name => ({ name, attending: false }))
  },
  ceremony: {
    partyMembers: names.map(name => ({ name, attending: false }))
  }
});

const ErrorMessage = styled.div`
  color: ${colors.api.error};
  font-family: 'GremeryTwo', cursive;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
  font-style: italic;
  opacity: 0.9;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-family: 'GremeryTwo', cursive;
  color: ${colors.neutral.darkBrown};
`;

const ThankYouContainer = styled.div`
  text-align: center;
  color: ${colors.neutral.darkBrown};
  margin-bottom: 2rem;
`;

const ThankYouTitle = styled.div`
  font-family: 'saint-andrews-queen', serif;
  font-size: 4.5rem;
  line-height: 1;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 3.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2.8rem;
    margin-bottom: 0.75rem;
  }
`;

const ThankYouMessage = styled.div`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.1rem;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.3;
    padding: 0 1rem;
  }
`;

const GuestInput = styled.input`
  padding: 0.75rem 0 1.25rem 0;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-family: 'GremeryTwo', cursive;
  transition: all 0.3s ease;
  color: #2c3e50;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #7f8c8d;
    opacity: 0.8;
    font-family: 'GremeryTwo', cursive;
  }
`;

const SelectedGuestInfo = styled.div`
  padding: 1rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid ${colors.primary.gold};
  margin-bottom: 1rem;

  @media (max-width: 600px) {
    align-items: flex-start;
    border-bottom-width: 1px;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
    padding: 0.75rem 0;
  }
`;

const GuestName = styled.span`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.1rem;
  color: #2c3e50;
  line-height: 1.35;
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.neutral.darkBrown};
  padding: 0.25rem 0;
  border-radius: 0;
  font-size: 0.95rem;
  font-family: 'GremeryTwo', cursive;
  font-weight: 400;
  cursor: pointer;
  transition: opacity 0.3s ease;
  letter-spacing: 0.25px;
  border: none;
  opacity: 0.55;
  
  &:hover {
    opacity: 0.9;
  }
`;

const SearchButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 1rem;
  width: 100%;
`;

const SearchButton = styled.button`
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: opacity 0.3s ease;
  position: relative;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const SearchButtonImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const SearchButtonText = styled.span`
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${colors.primary.lightTan};
  font-family: 'GremeryOne', serif;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 1px;
  pointer-events: none;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 0.95rem;
    letter-spacing: 0.5px;
  }
`;

const FormContainer = styled.div`
  background: transparent;
  border-radius: 20px;
  padding: 3rem;
  border: none;
  backdrop-filter: none;
  box-shadow: none;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem 0.75rem 2rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 600px) {
    gap: 1.15rem;
  }
`;

const PartyMemberRow = styled.div`
  margin-bottom: 0.5rem;

  @media (max-width: 600px) {
    margin-bottom: 0.25rem;
  }
`;

const EventSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const EventDescription = styled.p`
  font-family: 'GremeryTwo', cursive;
  color: rgba(28, 25, 18, 0.68);
  font-size: 0.95rem;
  line-height: 1.4;
  margin: -0.25rem 0 0.25rem;

  @media (max-width: 600px) {
    font-size: 0.9rem;
    margin-top: -0.15rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;

  @media (max-width: 600px) {
    gap: 0.65rem;
  }
`;

const Label = styled.label`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.25rem;
  color: ${colors.neutral.darkBrown};
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  letter-spacing: 0.3px;

  @media (max-width: 600px) {
    font-size: 1.1rem;
    margin-bottom: 0.15rem;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: -15px;
    width: 100%;
    height: 15px;
    background-image: url('/images/underline.png');
    background-repeat: no-repeat;
    background-size: 100% 100%;
    background-position: center bottom;
    opacity: 0.4;
    pointer-events: none;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 0 1.25rem 0;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-family: 'GremeryOne', cursive;
  line-height: 1.5;
  min-height: 80px;
  resize: none;
  transition: all 0.3s ease;
  color: #2c3e50;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #7f8c8d;
    opacity: 0.8;
    font-family: 'GremeryOne', cursive;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.4rem;
  }
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-family: 'GremeryTwo', cursive;
  font-size: 0.98rem;
  color: rgba(28, 25, 18, 0.78);
  line-height: 1.35;
  font-weight: 400;

  @media (max-width: 600px) {
    align-items: flex-start;
    padding: 0.2rem 0;
    width: 100%;
    font-size: 0.95rem;
  }
`;

const RadioInput = styled.input`
  accent-color: ${colors.primary.gold};

  @media (max-width: 600px) {
    flex-shrink: 0;
    margin-top: 0.2rem;
  }
`;

const RSVPFields = styled.div`
  margin-top: 2rem;

  @media (max-width: 600px) {
    margin-top: 1rem;
  }
`;

const RSVPFormComponent: React.FC = () => {
  const {
    selectedGuest,
    currentRSVP,
    loadingGuestSearch,
    loadingRSVP,
    submittingRSVP,
    guestSearchError,
    rsvpError,
    searchGuest,
    submitRSVP,
    updateRSVP,
    clearSelection
  } = useRSVP();

  const [guestName, setGuestName] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [formData, setFormData] = useState({
    attendance: '',
    eventResponses: buildEmptyEventResponses([]),
    message: ''
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Populate the editable reservation when a party is found.
  useEffect(() => {
    if (!selectedGuest) {
      setFormData({
        attendance: '',
        eventResponses: buildEmptyEventResponses([]),
        message: ''
      });
      return;
    }

    const fallbackCeremonyMembers = currentRSVP?.partyMembers || [];
    const eventResponses = RSVP_EVENTS.reduce((responses, event) => {
      const savedEvent = currentRSVP?.eventResponses?.[event.id];
      const savedMembers = savedEvent?.partyMembers
        || (event.id === 'ceremony' ? fallbackCeremonyMembers : []);

      responses[event.id] = {
        partyMembers: selectedGuest.searchableNames.map(name => {
          const savedMember = savedMembers.find(member => member.name === name);

          return {
            name,
            attending: savedMember?.attending ?? false
          };
        })
      };

      return responses;
    }, {} as EventResponses);

    setFormData({
      attendance: currentRSVP?.attendance || '',
      eventResponses,
      message: ''
    });
  }, [selectedGuest, currentRSVP]);

  const handleGuestSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim()) return;
    
    setHasSearched(true);
    setSubmitSuccess(false);
    await searchGuest(guestName.trim());
  };

  const handleGuestNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuestName(e.target.value);
    setSubmitSuccess(false);
    if (hasSearched) {
      clearSelection();
      setHasSearched(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEventMemberChange = (eventId: RSVPEventId, memberIndex: number, attending: boolean) => {
    setFormData(prev => ({
      ...prev,
      eventResponses: {
        ...prev.eventResponses,
        [eventId]: {
          ...prev.eventResponses[eventId],
          partyMembers: prev.eventResponses[eventId].partyMembers.map((member, index) => (
            index === memberIndex ? { ...member, attending } : member
          ))
        }
      }
    }));
  };

  const handleAttendanceChange = (attendance: 'yes' | 'no') => {
    setFormData(prev => ({
      ...prev,
      attendance,
      eventResponses: RSVP_EVENTS.reduce((responses, event) => {
        responses[event.id] = {
          partyMembers: prev.eventResponses[event.id].partyMembers.map(member => ({
            ...member,
            attending: attendance === 'yes' ? member.attending : false
          }))
        };

        return responses;
      }, {} as EventResponses)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedGuest) {
      alert('Please select a guest first.');
      return;
    }

    setSubmitSuccess(false);

    const normalizedEventResponses = RSVP_EVENTS.reduce((responses, event) => {
      const eventResponse = formData.eventResponses[event.id];

      responses[event.id] = {
        partyMembers: eventResponse.partyMembers.map(member => ({
          ...member,
          attending: formData.attendance === 'yes' ? member.attending : false
        }))
      };

      return responses;
    }, {} as EventResponses);

    const overallAttendance = formData.attendance as 'yes' | 'no';
    const ceremonyPartyMembers = normalizedEventResponses.ceremony.partyMembers;

    const rsvpData: RSVPData = {
      guestId: selectedGuest.id,
      attendance: overallAttendance,
      partyMembers: ceremonyPartyMembers,
      eventResponses: normalizedEventResponses,
      message: formData.message
    };

    let success = false;
    if (currentRSVP) {
      // Update existing RSVP
      success = await updateRSVP(currentRSVP.id, rsvpData);
    } else {
      // Submit new RSVP
      success = await submitRSVP(rsvpData);
    }

    if (success) {
      setSubmitSuccess(true);
    }
  };

  if (submitSuccess) {
    return (
      <FormContainer>
        <ThankYouContainer>
          <ThankYouTitle>Thank You!</ThankYouTitle>
          <ThankYouMessage>your RSVP has been saved. we can't wait to celebrate with you.</ThankYouMessage>
        </ThankYouContainer>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      {(guestSearchError || rsvpError) && (
        <ErrorMessage>
          {guestSearchError || rsvpError}
        </ErrorMessage>
      )}

      {loadingGuestSearch ? (
        <LoadingMessage>Looking up your party...</LoadingMessage>
      ) : (
        <>
          <FormGroup>
            {selectedGuest ? (
              <SelectedGuestInfo>
                <GuestName>
                  {selectedGuest.fullName}
                  {selectedGuest.searchableNames.length > 1 && (
                    <>
                      <br />
                      <small style={{ opacity: 0.7, fontSize: '0.9em' }}>
                        Party includes: {selectedGuest.searchableNames.join(', ')}
                      </small>
                    </>
                  )}
                </GuestName>
                <ClearButton
                  type="button"
                  onClick={() => {
                    clearSelection();
                    setGuestName('');
                    setHasSearched(false);
                    setSubmitSuccess(false);
                  }}
                >
                  search again
                </ClearButton>
              </SelectedGuestInfo>
            ) : (
              <form onSubmit={handleGuestSearch}>
                <InputWrapper>
                  <GuestInput
                    type="text"
                    id="guestName"
                    aria-label="Enter your full name"
                    value={guestName}
                    onChange={handleGuestNameChange}
                    placeholder="Enter your full name..."
                    disabled={loadingGuestSearch}
                    autoComplete="name"
                  />
                </InputWrapper>
                <SearchButtonWrapper>
                  <SearchButton 
                    type="submit" 
                    disabled={loadingGuestSearch || !guestName.trim()}
                  >
                    <SearchButtonImage src="/images/svgs/funky-save.svg" alt="" />
                    <SearchButtonText>
                      {loadingGuestSearch ? 'SEARCHING...' : 'SEARCH'}
                    </SearchButtonText>
                  </SearchButton>
                </SearchButtonWrapper>
              </form>
            )}
          </FormGroup>

          {selectedGuest && (
            <RSVPFields>
              {loadingRSVP ? (
                <LoadingMessage>Loading RSVP data...</LoadingMessage>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label>Will you be attending?</Label>
                    <RadioGroup>
                      <RadioOption>
                        <RadioInput
                          type="radio"
                          name="attendance"
                          value="yes"
                          checked={formData.attendance === 'yes'}
                          onChange={() => handleAttendanceChange('yes')}
                          required
                        />
                        Yes, I'll be there!
                      </RadioOption>
                      <RadioOption>
                        <RadioInput
                          type="radio"
                          name="attendance"
                          value="no"
                          checked={formData.attendance === 'no'}
                          onChange={() => handleAttendanceChange('no')}
                          required
                        />
                        Sorry, can't make it
                      </RadioOption>
                    </RadioGroup>
                  </FormGroup>

                  {formData.attendance === 'yes' && RSVP_EVENTS.map(event => (
                    <EventSection key={event.id}>
                      <Label>{event.title}</Label>
                      <EventDescription>{event.description}</EventDescription>
                      <RadioGroup>
                        {formData.eventResponses[event.id].partyMembers.map((member, index) => (
                          <PartyMemberRow key={`${event.id}-${member.name}`}>
                            <RadioOption>
                              <RadioInput
                                type="checkbox"
                                checked={member.attending}
                                onChange={(e) => handleEventMemberChange(event.id, index, e.target.checked)}
                              />
                              {member.name}
                            </RadioOption>
                          </PartyMemberRow>
                        ))}
                      </RadioGroup>
                    </EventSection>
                  ))}

                  <FormGroup>
                    <Label htmlFor="message">special message (optional)</Label>
                    <InputWrapper>
                      <TextArea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Any special message for the couple..."
                      />
                    </InputWrapper>
                  </FormGroup>

                  <SearchButton type="submit" disabled={submittingRSVP}>
                    <SearchButtonImage src="/images/svgs/funky-save.svg" alt="" />
                    <SearchButtonText>
                      {submittingRSVP 
                        ? 'SUBMITTING...' 
                        : currentRSVP 
                          ? 'UPDATE RSVP' 
                          : 'SUBMIT RSVP'}
                    </SearchButtonText>
                  </SearchButton>
                </Form>
              )}
            </RSVPFields>
          )}
        </>
      )}
    </FormContainer>
  );
};

export default RSVPFormComponent; 